import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical, Send, Image, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Quote } from '../types';

interface QuoteActionsDropdownProps {
  quote: Quote;
  onRemoveFromQuotewall: () => void;
}

export default function QuoteActionsDropdown({ quote, onRemoveFromQuotewall }: QuoteActionsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAction = (action: 'style' | 'card' | 'remove') => {
    setIsOpen(false);
    
    switch (action) {
      case 'style':
        navigate(`/send/style/${quote.id}`);
        break;
      case 'card':
        navigate(`/send/card/${quote.id}`);
        break;
      case 'remove':
        onRemoveFromQuotewall();
        break;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
        aria-label="Quote actions"
      >
        <MoreVertical className="h-5 w-5" />
      </button>

      {isOpen && (
        <div 
          className="fixed w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50"
          style={{
            top: buttonRef.current ? Math.min(
              buttonRef.current.getBoundingClientRect().bottom + 8,
              window.innerHeight - 200 // Leave space for the dropdown
            ) : 0,
            left: buttonRef.current ? 
              Math.min(
                buttonRef.current.getBoundingClientRect().left - 200, // Position to the left of the button
                window.innerWidth - 224 // 224 = dropdown width (w-56) + some padding
              ) : 0
          }}
        >
          <div className="py-1" role="menu">
            <button
              onClick={() => handleAction('style')}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              role="menuitem"
            >
              <Send className="h-4 w-4" />
              Send a quote (style)
            </button>
            <button
              onClick={() => handleAction('card')}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              role="menuitem"
            >
              <Image className="h-4 w-4" />
              Send a quote card (with pic)
            </button>
            {quote.quotewall && (
              <button
                onClick={() => handleAction('remove')}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                role="menuitem"
              >
                <X className="h-4 w-4" />
                Remove from Quotewall
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}